import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  GenerateOptionsFromQuizQuestionInputPropsType,
  GenerativeTaskStatusEnum,
  GenerativeTaskTypeEnum,
} from '@/types/schema';

import useGenerativeTask from '../useGenerativeTask';

export type Suggestion = {
  questionOption: string;
  generativeTaskId: string;
};

interface Params {
  input: Pick<GenerateOptionsFromQuizQuestionInputPropsType, 'question' | 'correctOptions'>;
}

const useQuestionOptions = ({ input }: Params) => {
  const [suggestionsData, setSuggestionsData] = useState<Suggestion[]>([]);
  const {
    executeCreateGenerativeTask,
    fetching: isCreatingGenerativeTask,
    generativeTask,
    generativeTaskId,
    generativeTaskIdFromSubscription,
  } = useGenerativeTask({});

  const suggestionsFromSubscription: Suggestion[] = useMemo(() => {
    const result = generativeTask?.result;
    const data = result?.optionsFromQuizQuestion?.records;

    return (
      data?.map((datum) => ({
        questionOption: datum.value,
        generativeTaskId: generativeTaskIdFromSubscription || generativeTaskId,
      })) || []
    );
  }, [generativeTask?.result, generativeTaskId, generativeTaskIdFromSubscription]);

  useEffect(() => {
    if (
      suggestionsFromSubscription.length &&
      generativeTaskId === generativeTaskIdFromSubscription &&
      !isCreatingGenerativeTask
    ) {
      setSuggestionsData(suggestionsFromSubscription);
    }
  }, [
    generativeTaskId,
    generativeTaskIdFromSubscription,
    isCreatingGenerativeTask,
    suggestionsFromSubscription,
  ]);

  const onGenerate = useCallback(() => {
    setSuggestionsData([]);
    executeCreateGenerativeTask({
      type: GenerativeTaskTypeEnum.OptionsFromQuizQuestion,
      input: {
        optionsFromQuizQuestion: input,
      },
    });
  }, [executeCreateGenerativeTask, input]);

  const isGenerating =
    generativeTask?.status &&
    [GenerativeTaskStatusEnum.ToBeProcessed, GenerativeTaskStatusEnum.Processing].includes(
      generativeTask.status,
    );

  const onAccceptSuggestion = useCallback((suggestion: Suggestion) => {
    setSuggestionsData((prevSuggestionsData) =>
      prevSuggestionsData.filter(
        (prevSuggestionData) => prevSuggestionData.questionOption !== suggestion.questionOption,
      ),
    );
  }, []);

  return {
    generativeTaskId,
    suggestions: suggestionsData,
    isCreatingGenerativeTask,
    isGenerating,
    onGenerate,
    onAccceptSuggestion,
  };
};

export default useQuestionOptions;
